import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Plus, Edit3, Trash2, Users, Image as ImageIcon, ArrowLeft, Loader2, LogOut, Home,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import EventPhotoUploader from '@/components/EventPhotoUploader';
import PromoCodesPanel from '@/components/PromoCodesPanel';
import api from '@/lib/api';

const CITIES = [
  'Los Angeles', 'San Diego', 'San Francisco', 'Bay Area', 'Sacramento',
  'Orange County', 'San Jose', 'Santa Barbara', 'Palm Springs', 'Other California',
];

const empty = () => ({
  title: '', description: '', event_date: '', location: '', city: '',
  max_capacity: 50, price: '', member_discount_price: '', photo_url: '', photos: [],
});

const formatDateTime = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return '—'; }
};

const statusBadge = (status) => {
  const styles = {
    Upcoming: 'bg-green-100 text-green-700 border-green-300',
    Full: 'bg-orange-100 text-orange-700 border-orange-300',
    Past: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  return styles[status] || 'bg-gray-100 text-gray-600';
};

export default function CoAdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [submitting, setSubmitting] = useState(false);

  const [rsvpsOpen, setRsvpsOpen] = useState(false);
  const [rsvpsData, setRsvpsData] = useState(null);

  const [photosOpen, setPhotosOpen] = useState(false);
  const [photosEvent, setPhotosEvent] = useState(null);

  const isCoAdmin = !!user?.can_manage_events && !user?.is_admin;
  const isAdmin = !!user?.is_admin;

  // Gate access
  useEffect(() => {
    if (user && !isCoAdmin && !isAdmin) {
      toast.error('Co-Admin access required');
      navigate('/dashboard');
    }
  }, [user, isCoAdmin, isAdmin, navigate]);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/events/mine');
      setEvents(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const openCreate = () => { setForm(empty()); setCreateOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      event_date: ev.event_date ? ev.event_date.slice(0, 16) : '',
      location: ev.location || '',
      city: ev.city || '',
      max_capacity: ev.max_capacity || 50,
      price: ev.price || '',
      member_discount_price: ev.member_discount_price ?? '',
      photo_url: ev.photo_url || '',
      photos: ev.photos || [],
    });
    setEditOpen(true);
  };

  const submitForm = async (mode) => {
    const errs = [];
    if (!form.title) errs.push('title');
    if (!form.event_date) errs.push('date');
    if (!form.city) errs.push('city');
    if (errs.length) {
      toast.error(`Missing: ${errs.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        max_capacity: parseInt(form.max_capacity, 10) || 50,
        price: form.price ? parseFloat(form.price) : 0,
        event_date: new Date(form.event_date).toISOString(),
      };
      if (mode === 'create') {
        await api.post('/events', payload);
        toast.success('Event created — admins have been notified');
        setCreateOpen(false);
      } else {
        await api.patch(`/events/${editing.id}`, payload);
        toast.success('Event updated');
        setEditOpen(false);
        setEditing(null);
      }
      await loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSubmitting(false);
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This will remove all RSVPs and cannot be undone.`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success('Event deleted');
      await loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openRsvps = async (ev) => {
    try {
      const res = await api.get(`/events/${ev.id}/rsvps`);
      setRsvpsData(res.data);
      setRsvpsOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load RSVPs');
    }
  };

  const openPhotos = (ev) => {
    setPhotosEvent(ev);
    setPhotosOpen(true);
  };

  const photosOf = (ev) => ev?.photos || (ev?.photo_url ? [ev.photo_url] : []);

  return (
    <div className="min-h-screen bg-cream/30" data-testid="coadmin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-text-mid hover:text-navy" title="Back to member dashboard">
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-display text-xl text-navy">Co-Admin Dashboard</h1>
            <Badge className="bg-teal/10 text-teal-deep border-teal/20">Events Co-Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
              data-testid="coadmin-back-to-member"
            >
              <Home size={16} className="mr-1" /> Member View
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate('/login'); }}
              className="text-red-600 hover:text-red-700 rounded-full"
            >
              <LogOut size={16} className="mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6 gap-3 flex-wrap"
        >
          <div>
            <h2 className="font-display text-2xl text-navy">My Events</h2>
            <p className="text-text-mid text-sm mt-1">Create and manage the events you host. Admins are notified of each change.</p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-teal hover:bg-teal-deep text-white rounded-full"
            data-testid="coadmin-create-event-btn"
          >
            <Plus size={18} className="mr-2" /> Create New Event
          </Button>
        </motion.div>

        <div className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid="coadmin-events-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event Name</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-center">RSVP / Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-text-mid">Loading…</TableCell></TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Calendar size={28} className="mx-auto text-text-light mb-2 opacity-40" />
                    <p className="text-text-mid">You haven&apos;t created any events yet.</p>
                    <p className="text-xs text-text-light mt-1">Click &ldquo;Create New Event&rdquo; to host your first one.</p>
                  </TableCell>
                </TableRow>
              ) : events.map((ev) => (
                <TableRow key={ev.id} data-testid={`coadmin-event-row-${ev.id}`}>
                  <TableCell className="font-medium text-navy">{ev.title}</TableCell>
                  <TableCell className="text-sm">{formatDateTime(ev.event_date)}</TableCell>
                  <TableCell className="text-sm text-text-mid">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {ev.city}{ev.location ? ` • ${ev.location}` : ''}</span>
                  </TableCell>
                  <TableCell className="text-center text-sm">{ev.rsvp_count || 0} / {ev.max_capacity || 0}</TableCell>
                  <TableCell><Badge className={statusBadge(ev.status)} variant="outline">{ev.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} className="text-teal hover:bg-teal/10" data-testid={`coadmin-edit-${ev.id}`}>
                        <Edit3 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openRsvps(ev)} className="text-blue-600 hover:bg-blue-50" data-testid={`coadmin-rsvps-${ev.id}`}>
                        <Users size={14} className="mr-1" /> RSVPs
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openPhotos(ev)} className="text-amber-700 hover:bg-amber-50" data-testid={`coadmin-photos-${ev.id}`}>
                        <ImageIcon size={14} className="mr-1" /> Photos
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(ev)} className="text-red-600 hover:bg-red-50" data-testid={`coadmin-delete-${ev.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {(user?.can_manage_promo_codes || user?.is_admin) && (
          <PromoCodesPanel />
        )}
      </main>

      {/* Create/Edit Dialog (shared) */}
      {[
        { open: createOpen, setOpen: setCreateOpen, mode: 'create', title: 'Create New Event' },
        { open: editOpen, setOpen: setEditOpen, mode: 'edit', title: 'Edit Event' },
      ].map(({ open, setOpen, mode, title }) => (
        <Dialog key={mode} open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`coadmin-${mode}-event-dialog`}>
            <DialogHeader>
              <DialogTitle className="font-display text-navy">{title}</DialogTitle>
              <DialogDescription>Fill in the details below. All times are local.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label>Event Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid={`coadmin-${mode}-title`} />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Date &amp; Time *</Label>
                  <Input type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="rounded-xl" data-testid={`coadmin-${mode}-date`} />
                </div>
                <div className="space-y-1.5">
                  <Label>City *</Label>
                  <select
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm h-10"
                    data-testid={`coadmin-${mode}-city`}
                  >
                    <option value="">Select a city</option>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Location / Venue</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl" placeholder="e.g. Café Verve, Pasadena" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Max Capacity</Label>
                  <Input type="number" min={1} value={form.max_capacity} onChange={(e) => setForm({ ...form, max_capacity: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label>Ticket Price (USD, optional)</Label>
                  <Input type="number" min={0} step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0 for free" className="rounded-xl" />
                </div>
              </div>
              {parseFloat(form.price) > 0 && (
                <div className="space-y-1.5">
                  <Label>Member Price — Connect / Thrive (optional)</Label>
                  <Input
                    type="number" min={0} step="0.01"
                    value={form.member_discount_price}
                    onChange={(e) => setForm({ ...form, member_discount_price: e.target.value })}
                    placeholder={`Below $${Number(form.price || 0).toFixed(2)} for a discount`}
                    className="rounded-xl"
                    data-testid={`coadmin-${mode}-member-price`}
                  />
                  <p className="text-xs text-text-mid">Leave blank if members pay the regular price.</p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Event Photos (optional)</Label>
                <EventPhotoUploader
                  value={form.photos}
                  onChange={(arr) => setForm({ ...form, photos: arr, photo_url: arr[0] || '' })}
                  testIdPrefix={`coadmin-${mode}-photos`}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={() => submitForm(mode)}
                disabled={submitting}
                className="bg-teal hover:bg-teal-deep rounded-full"
                data-testid={`coadmin-${mode}-submit-btn`}
              >
                {submitting && <Loader2 size={16} className="animate-spin mr-2" />}
                {mode === 'create' ? 'Create Event' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ))}

      {/* RSVPs Dialog */}
      <Dialog open={rsvpsOpen} onOpenChange={setRsvpsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="coadmin-rsvps-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{rsvpsData?.event?.title} — RSVPs</DialogTitle>
            <DialogDescription>{rsvpsData?.rsvp_count || 0} total · capacity {rsvpsData?.event?.max_capacity || 0}</DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>RSVP&apos;d At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rsvpsData?.rsvps || []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.user_name}</TableCell>
                  <TableCell className="text-sm">{r.user_email}</TableCell>
                  <TableCell><Badge className={r.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>{r.status}</Badge></TableCell>
                  <TableCell className="text-xs text-text-mid">{formatDateTime(r.rsvp_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Photos Dialog */}
      <Dialog open={photosOpen} onOpenChange={setPhotosOpen}>
        <DialogContent className="max-w-2xl" data-testid="coadmin-photos-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Event Photos — {photosEvent?.title}</DialogTitle>
            <DialogDescription>Drag and drop or click to add photos. Up to 4 per event.</DialogDescription>
          </DialogHeader>
          <EventPhotoUploader
            value={photosOf(photosEvent)}
            onChange={async (next) => {
              if (!photosEvent) return;
              try {
                await api.patch(`/events/${photosEvent.id}`, {
                  photos: next,
                  photo_url: next[0] || null,
                });
                const updated = { ...photosEvent, photos: next, photo_url: next[0] || null };
                setPhotosEvent(updated);
                await loadEvents();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to update photos');
              }
            }}
            testIdPrefix="coadmin-event-photos"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
